import { useState } from "react";
import { toast } from "sonner";
import { Plus, RotateCcw } from "lucide-react";
import { usePoll } from "@/hooks/usePoll";
import { api, errMsg, fmt, money } from "@/lib/api";
import TradeDialog from "@/components/TradeDialog";
import { SignalBadge, Pnl, PageHeader } from "@/components/Badges";

const Kpi = ({ label, children, testid }) => (
  <div className="panel p-4" data-testid={testid}><p className="label-xs mb-2">{label}</p><div className="num text-xl text-zinc-900">{children}</div></div>
);

function ResetBox({ onDone }) {
  const [bal, setBal] = useState("10000");
  const [ccy, setCcy] = useState("USD");
  const reset = async () => {
    if (!window.confirm("Reset paper account? All paper trades will be deleted.")) return;
    try { await api.post("/paper/account/reset", { balance: Number(bal), currency: ccy }); toast.success("Paper account reset"); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex items-center gap-2" data-testid="reset-account-box">
      {["10000", "25000", "50000"].map((b) => <button key={b} onClick={() => setBal(b)} className={`px-2 py-1 text-[11px] num border rounded-sm ${bal === b ? "border-[#007AFF] text-zinc-900" : "border-zinc-200 text-zinc-500"}`} data-testid={`preset-balance-${b}`}>{Number(b) / 1000}k</button>)}
      <input value={bal} onChange={(e) => setBal(e.target.value)} className="w-24 bg-white border border-zinc-200 rounded-sm px-2 py-1 num text-xs" data-testid="reset-balance-input" />
      <select value={ccy} onChange={(e) => setCcy(e.target.value)} className="bg-white border border-zinc-200 rounded-sm px-2 py-1 text-xs" data-testid="reset-currency-select">
        <option>USD</option><option>EUR</option><option>GBP</option>
      </select>
      <button onClick={reset} className="flex items-center gap-1 px-3 py-1.5 text-xs border border-zinc-300 rounded-sm hover:border-zinc-400 transition-colors" data-testid="reset-account-btn"><RotateCcw size={12} />Reset</button>
    </div>
  );
}

export default function PaperTrading() {
  const acct = usePoll("/paper/account", 10000);
  const open = usePoll("/paper/trades?status=open", 10000);
  const closed = usePoll("/paper/trades?status=closed", 30000);
  const [dlg, setDlg] = useState(false);
  const reload = () => { acct.reload(); open.reload(); closed.reload(); };
  const a = acct.data;
  const ccy = a?.currency || "USD";
  const close = async (id) => {
    try { const r = await api.post(`/paper/trades/${id}/close`); toast.success(`Closed · P/L ${money(r.data.pnl, ccy)}`); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const dg = (s) => (s.endsWith("JPY") ? 3 : 5);
  return (
    <div data-testid="paper-trading-page">
      <PageHeader title="Paper Trading" sub="Virtual account · no real money involved" right={
        <div className="flex flex-wrap items-center gap-3">
          <ResetBox onDone={reload} />
          <button onClick={() => setDlg(true)} className="flex items-center gap-1 px-4 py-2 text-xs font-semibold bg-[#007AFF] hover:bg-[#1a86ff] rounded-sm transition-colors text-white" data-testid="new-trade-btn"><Plus size={14} />New Trade</button>
        </div>} />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <Kpi label="Balance" testid="paper-balance">{money(a?.balance, ccy)}</Kpi>
        <Kpi label="Equity" testid="paper-equity">{money(a?.equity, ccy)}</Kpi>
        <Kpi label="Unrealized P/L" testid="paper-unrealized"><Pnl v={a?.unrealized_pnl}>{money(a?.unrealized_pnl, ccy)}</Pnl></Kpi>
        <Kpi label="Realized P/L" testid="paper-realized"><Pnl v={a?.realized_pnl}>{money(a?.realized_pnl, ccy)}</Pnl></Kpi>
        <Kpi label="Starting Balance" testid="paper-initial">{money(a?.initial_balance, ccy)}</Kpi>
      </div>
      <section className="panel mb-4">
        <h3 className="px-4 py-3 border-b border-zinc-200 font-display font-bold text-sm">Open Positions <span className="num text-zinc-500">({open.data?.length || 0})</span></h3>
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm" data-testid="open-trades-table">
            <thead className="bg-zinc-50"><tr>{["Pair", "Side", "Lots", "Entry", "Current", "SL", "TP", "Pips", "P/L", ""].map((h) => <th key={h} className="px-3 py-2 text-left label-xs">{h}</th>)}</tr></thead>
            <tbody>
              {(open.data || []).map((t) => (
                <tr key={t.id} className="border-t border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`open-trade-${t.id}`}>
                  <td className="px-3 py-2.5 num font-bold">{t.symbol}</td><td className="px-3 py-2.5"><SignalBadge signal={t.direction} /></td>
                  <td className="px-3 py-2.5 num text-xs">{t.lots}</td><td className="px-3 py-2.5 num text-xs">{fmt(t.entry, dg(t.symbol))}</td>
                  <td className="px-3 py-2.5 num text-xs">{fmt(t.current_price, dg(t.symbol))}</td>
                  <td className="px-3 py-2.5 num text-xs text-[#DC2626]/90">{t.stop_loss ? fmt(t.stop_loss, dg(t.symbol)) : "—"}</td>
                  <td className="px-3 py-2.5 num text-xs text-[#059669]/90">{t.take_profit ? fmt(t.take_profit, dg(t.symbol)) : "—"}</td>
                  <td className="px-3 py-2.5 text-xs"><Pnl v={t.pips}>{t.pips}</Pnl></td>
                  <td className="px-3 py-2.5 text-xs"><Pnl v={t.unrealized_pnl} testid={`open-trade-pnl-${t.id}`}>{money(t.unrealized_pnl, ccy)}</Pnl></td>
                  <td className="px-3 py-2.5"><button onClick={() => close(t.id)} className="px-3 py-1 text-[11px] border border-zinc-300 rounded-sm hover:border-[#DC2626] hover:text-[#DC2626] transition-colors" data-testid={`close-trade-${t.id}`}>Close</button></td>
                </tr>
              ))}
              {!open.data?.length && <tr><td colSpan={10} className="p-6 text-center text-sm text-zinc-500" data-testid="no-open-trades">No open positions. Accept a signal or open a manual trade.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
      <section className="panel">
        <h3 className="px-4 py-3 border-b border-zinc-200 font-display font-bold text-sm">Trade History</h3>
        <div className="overflow-x-auto scrollbar-thin max-h-[420px]">
          <table className="w-full text-sm" data-testid="closed-trades-table">
            <thead className="bg-zinc-50"><tr>{["Pair", "Side", "Lots", "Entry", "Exit", "Reason", "Pips", "P/L", "Closed"].map((h) => <th key={h} className="px-3 py-2 text-left label-xs">{h}</th>)}</tr></thead>
            <tbody>
              {(closed.data || []).map((t) => (
                <tr key={t.id} className="border-t border-zinc-100" data-testid={`closed-trade-${t.id}`}>
                  <td className="px-3 py-2 num font-bold">{t.symbol}</td><td className="px-3 py-2"><SignalBadge signal={t.direction} /></td>
                  <td className="px-3 py-2 num text-xs">{t.lots}</td><td className="px-3 py-2 num text-xs">{fmt(t.entry, dg(t.symbol))}</td>
                  <td className="px-3 py-2 num text-xs">{fmt(t.close_price, dg(t.symbol))}</td><td className="px-3 py-2 text-xs text-zinc-500">{t.close_reason}</td>
                  <td className="px-3 py-2 text-xs"><Pnl v={t.pips}>{t.pips}</Pnl></td><td className="px-3 py-2 text-xs"><Pnl v={t.pnl}>{money(t.pnl, ccy)}</Pnl></td>
                  <td className="px-3 py-2 num text-[11px] text-zinc-500">{new Date(t.closed_at).toUTCString().slice(5, 22)}</td>
                </tr>
              ))}
              {!closed.data?.length && <tr><td colSpan={9} className="p-6 text-center text-sm text-zinc-500">No closed trades yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
      <TradeDialog open={dlg} onOpenChange={setDlg} onDone={reload} />
    </div>
  );
}
